<template>
  <div class="question-container bg-secondary-3">
    <div
      v-for="(question, questionIndex) in questionsList"
      :key="questionIndex"
      class="question-item row mx-auto col px-0"
    >
      <div
        class="question-box-content row mx-auto col-12 d-flex justify-content-between align-items-end px-0"
      >
        <p class="col px-0 color-gray-6">
          {{ question.description }}
        </p>
        <div
          class="custom-radio-container d-flex align-items-end px-0"
          :class="{
            col: !removeColHeader,
          }"
        >
          <div
            v-for="(answer, answerIndex) in question.possibleAnswers"
            :id="`rbAnswersContainer-${answerIndex}`"
            :key="answerIndex"
            class="text-center rounded-top mb-0 custom-radio-button"
            :class="{
              'bg-primary-1': question.submittedAnswerTypeId === answer.id,
              'bg-secondary-9': question.submittedAnswerTypeId !== answer.id,
              'bg-red': false,
            }"
          >
            <div :id="`radio-answer-${answerIndex}`" class="radio">
              <label
                :id="`lbl-radio-answer-${answerIndex}`"
                class="radio-label w-100 mb-0"
              >
                <input
                  :id="`radio-button-answer-${answerIndex}`"
                  type="radio"
                  :name="`radio-answer-YesNoOption`"
                  :value="question.submittedAnswerTypeId === answer.id"
                  :disabled="isReadonly"
                  @change="updateSelectedAnswer(question.id, answer.id)"
                />
                <span
                  :id="`span-icon-${answerIndex}`"
                  class="cr rounded-circle"
                  :class="{
                    'cr-inactive': question.submittedAnswerTypeId !== answer.id,
                    'cr-active': question.submittedAnswerTypeId === answer.id,
                    'cr-error': false,
                  }"
                >
                  <i
                    :id="`answer-icon-${answerIndex}`"
                    class="cr-icon fa fa-check text-white"
                    :class="{
                      'show-check':
                        question.submittedAnswerTypeId === answer.id,
                    }"
                  />
                </span>
              </label>
            </div>
            <span
              class="step-label font-12"
              :class="{
                'color-gray-8': question.submittedAnswerTypeId !== answer.id,
                'color-white fw-bold':
                  question.submittedAnswerTypeId === answer.id,
              }"
            >
              {{ answer.name }}
            </span>
          </div>
        </div>
      </div>
      <Error
        v-if="validate && question.submittedAnswerTypeId == null"
        :is-alert-mode="false"
        class="mt-0"
        variant="no-border"
        message="Favor de seleccionar Sí o No"
      />
    </div>
    <div class="d-flex justify-content-end">
      <action-button
        v-if="!isReadonly"
        type="button"
        text="Guardar"
        i="fa fa-save pr-2"
        variant="success"
        @click="onSaveQuestions"
      />
    </div>
  </div>
</template>

<script>
import store from '@/store/index';
import peiCode from '@/utils/constants/pei/peiCode';
import AnswerType from '@/utils/constants/pei/serviceNeedQuestionnaire/answerType';
import needsService from '@/services/pei/serviceNeedQuestionnaire';
import ActionButton from 'ActionButton';
import Error from '@/components/common/Error';

const Questions = {
  DoesStudentUsesWheelchairForMovingOneToAnotherPlace: 9,
  DoesTheStudentUseMotorizedWheelchair: 10,
  IsStudentAbleToMoveAroundOnHisHerOwn: 11,
};

export default {
  name: 'QuestionsWheelChair',
  components: {
    ActionButton,
    Error,
  },
  props: {
    peiId: {
      type: String,
      default: () => '',
    },
    removeColHeader: {
      type: Boolean,
      default: false,
    },
    isReadonly: {
      type: Boolean,
      default: () => false,
    },
    validate: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      questions: [],
    };
  },
  async mounted() {
    if (!this.questions?.length) {
      await this.getQuestions();
    }
  },
  computed: {
    selectedPeiId() {
      return store.getters.SelectedPei;
    },
    questionsList() {
      let _questions = [...this.questions];

      const studentIsUsingWheelchairForMovingOneToAnotherPlaceQuestion = this.questions.find(
        (x) =>
          x.id === Questions.DoesStudentUsesWheelchairForMovingOneToAnotherPlace
      );
      if (
        studentIsUsingWheelchairForMovingOneToAnotherPlaceQuestion?.submittedAnswerTypeId !==
        AnswerType.Yes
      ) {
        _questions = _questions.filter(
          (x) =>
            x.id ===
            Questions.DoesStudentUsesWheelchairForMovingOneToAnotherPlace
        );
      }

      const studentUsesMotorizedWheelchairQuestion = this.questions.find(
        (x) => x.id === Questions.DoesTheStudentUseMotorizedWheelchair
      );
      if (
        studentUsesMotorizedWheelchairQuestion?.submittedAnswerTypeId !==
        AnswerType.No
      ) {
        _questions = _questions.filter(
          (x) => x.id !== Questions.IsStudentAbleToMoveAroundOnHisHerOwn
        );
      }

      return _questions;
    },
    studentIsUsingWheelchairForMovingOneToAnotherPlace() {
      return (questionItem) =>
        questionItem.id ===
          Questions.DoesStudentUsesWheelchairForMovingOneToAnotherPlace &&
        questionItem.submittedAnswerTypeId === AnswerType.Yes;
    },
    studentIsNotUsingMotorizedWheelchair() {
      return (questionItem) =>
        questionItem.id === Questions.DoesTheStudentUseMotorizedWheelchair &&
        questionItem.submittedAnswerTypeId === AnswerType.No;
    },
    studentIsNotAbleToMoveAroundOnHisHerOwn() {
      return (questionItem) =>
        questionItem.id === Questions.IsStudentAbleToMoveAroundOnHisHerOwn &&
        questionItem.submittedAnswerTypeId === AnswerType.No;
    },
  },
  methods: {
    async getQuestions() {
      const _questions = await needsService.getQuestions({
        peiId: this.selectedPeiId ?? this.peiId,
        peiCodeId: peiCode.AsistenteSillaRuedas,
      });
      this.questions = _questions.map((questionItem) => ({
        ...questionItem,
        documentModel: null,
        uploadDocumentPanel: false,
        possibleAnswers: questionItem.possibleAnswers.filter((item) =>
          [AnswerType.Yes, AnswerType.No].includes(item.id)
        ),
      }));
    },
    updateSelectedAnswer(questionId, answerId) {
      const currentQuestion = this.questions.find((x) => x.id === questionId);

      if (!currentQuestion?.id) return;

      currentQuestion.submittedAnswerTypeId = answerId;
    },
    async onSaveQuestions() {
      const payload = this.questionsList.map((questionItem) => ({
        id: questionItem.submittedId,
        peiId: this.selectedPeiId,
        questionServiceNeedId: questionItem.id,
        answerTypeId: questionItem.submittedAnswerTypeId,
        document: null,
        comment: null,
      }));

      const studentIsUsingWheelchairForMovingOneToAnotherPlaceValue = this.questionsList.some(
        (questionItem) =>
          this.studentIsUsingWheelchairForMovingOneToAnotherPlace(questionItem)
      );
      const studentIsNotUsingMotorizedWheelchairValue = this.questionsList.some(
        (questionItem) =>
          this.studentIsNotUsingMotorizedWheelchair(questionItem)
      );
      const studentIsNotAbleToMoveAroundOnHisHerOwnValue = this.questionsList.some(
        (questionItem) =>
          this.studentIsNotAbleToMoveAroundOnHisHerOwn(questionItem)
      );
      const justified =
        studentIsUsingWheelchairForMovingOneToAnotherPlaceValue &&
        studentIsNotUsingMotorizedWheelchairValue &&
        studentIsNotAbleToMoveAroundOnHisHerOwnValue;

      this.$emit('save-questions', {
        answers: payload,
        isJustified: justified,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables/_colors.scss';

.question-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 20px 36px;
}
.question-item {
  gap: 30px;
}
.question-box-content {
  border-bottom: solid 1px map-get($colors, 'gray-5');
}
.custom-radio-container {
  padding-top: 15px;
}
.custom-radio-button {
  width: 60px;
  height: 60px;
  margin-left: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.custom-radio-button,
.radio-label {
  cursor: pointer;
}
.show-check {
  opacity: 1 !important;
}
.step-label {
  display: block;
  font-family: Lato;
  font-weight: 500;
  line-height: 160%;
}

.cr.rounded-circle.cr-inactive {
  border-color: map-get($colors, 'gray-8');
}
.cr.rounded-circle.cr-active {
  border-color: map-get($colors, 'white');
}
.cr-error {
  border-color: map-get($colors, 'red');
}
/************* custom radio *****************/
.checkbox label:after,
.radio label:after {
  content: '';
  display: table;
  clear: both;
}

.checkbox .cr,
.radio .cr {
  position: relative;
  display: inline-block;
  border: 2px solid map-get($colors, 'white');
  width: 1.2em;
  height: 1.2em;
  margin-top: 4px;
}

.checkbox .cr .cr-icon,
.radio .cr .cr-icon {
  position: absolute;
  font-size: 0.6em;
  line-height: 0;
  top: 50%;
  left: 20%;
}

.cr-icon {
  margin-top: 0.04em;
}

.checkbox label input[type='checkbox'],
.radio label input[type='radio'] {
  display: none;
}

.checkbox label input[type='checkbox'] + .cr > .cr-icon,
.radio label input[type='radio'] + .cr > .cr-icon {
  opacity: 0;
}

.checkbox label input[type='checkbox']:checked + .cr > .cr-icon,
.radio label input[type='radio']:checked + .cr > .cr-icon {
  opacity: 1;
}

.checkbox label input[type='checkbox']:disabled + .cr,
.radio label input[type='radio']:disabled + .cr {
  opacity: 0.5;
}
/************* end of custom radio *****************/
</style>
